import { supabase } from '../../supabaseClient';

const bucketName = 'aircraft-documents';

export async function fetchAircraftDocuments(aircraftId) {
  const { data, error } = await supabase
    .from('aircraft_documents')
    .select(
      'id, aircraft_id, organisation_id, document_type, file_name, file_path, mime_type, file_size, expiry_date, uploaded_by, created_at'
    )
    .eq('aircraft_id', aircraftId)
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  return data || [];
}

export async function uploadAircraftDocument({
  aircraftId,
  organisationId,
  documentType,
  expiryDate,
  file,
  userId,
}) {
  if (!aircraftId) {
    throw new Error('Aircraft ID is missing.');
  }

  if (!organisationId) {
    throw new Error('Organisation ID is missing.');
  }

  if (!file) {
    throw new Error('Please select a document.');
  }

  const safeFileName = sanitiseFileName(file.name);

  const filePath = [
    organisationId,
    aircraftId,
    `${Date.now()}_${safeFileName}`,
  ].join('/');

  const { error: uploadError } = await supabase.storage
    .from(bucketName)
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
      contentType:
        file.type || 'application/octet-stream',
    });

  if (uploadError) {
    throw uploadError;
  }

  const { data, error: insertError } = await supabase
    .from('aircraft_documents')
    .insert({
      aircraft_id: aircraftId,
      organisation_id: organisationId,
      document_type: documentType,
      file_name: file.name,
      file_path: filePath,
      mime_type:
        file.type || 'application/octet-stream',
      file_size: file.size,
      expiry_date: expiryDate || null,
      uploaded_by: userId || null,
    })
    .select(
      'id, aircraft_id, organisation_id, document_type, file_name, file_path, mime_type, file_size, expiry_date, uploaded_by, created_at'
    )
    .single();

  if (insertError) {
    await supabase.storage
      .from(bucketName)
      .remove([filePath]);

    throw insertError;
  }

  return data;
}

export async function deleteAircraftDocument(document) {
  if (!document?.id) {
    throw new Error('Document ID is missing.');
  }

  if (document.file_path) {
    const { error: storageError } =
      await supabase.storage
        .from(bucketName)
        .remove([document.file_path]);

    if (storageError) {
      throw storageError;
    }
  }

  const { error: databaseError } = await supabase
    .from('aircraft_documents')
    .delete()
    .eq('id', document.id);

  if (databaseError) {
    throw databaseError;
  }
}

export async function createAircraftDocumentUrl(
  filePath
) {
  if (!filePath) {
    throw new Error('Document file path is missing.');
  }

  const { data, error } = await supabase.storage
    .from(bucketName)
    .createSignedUrl(filePath, 60 * 10);

  if (error) {
    throw error;
  }

  if (!data?.signedUrl) {
    throw new Error(
      'Could not create document link.'
    );
  }

  return data.signedUrl;
}

function sanitiseFileName(fileName) {
  return String(fileName || 'document')
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, '_');
}